import { useEffect, useState } from 'react'
import { WHATS_NEW, type WhatsNewEntry } from '@/content/whatsNew'
import { compareSemver } from '@/lib/semver'
import { TEST_IDS } from '@/lib/testIds'
import { Button } from '@/ui/shadcn/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/ui/shadcn/dialog'
import { markWhatsNewSeen, useWhatsNewLastSeenVersion } from '@/app/whatsNew/storage'

const NOTICE_ITEM_LIMIT = 6

interface VisibleWhatsNew {
  entries: WhatsNewEntry[]
  remainingItems: number
}

function visibleWhatsNew(entries: readonly WhatsNewEntry[], itemLimit?: number): VisibleWhatsNew {
  const totalItems = entries.reduce((total, entry) => total + entry.items.length, 0)
  if (itemLimit === undefined || totalItems <= itemLimit) {
    return { entries: entries.map((entry) => ({ ...entry })), remainingItems: 0 }
  }
  let remainingSlots = itemLimit
  const visibleEntries = entries.flatMap((entry) => {
    if (remainingSlots === 0) return []
    const items = entry.items.slice(0, remainingSlots)
    remainingSlots -= items.length
    return items.length > 0 ? [{ ...entry, items }] : []
  })
  return { entries: visibleEntries, remainingItems: totalItems - itemLimit }
}

function WhatsNewList({
  entries,
  itemLimit,
  onShowMore,
}: {
  entries: readonly WhatsNewEntry[]
  itemLimit?: number
  onShowMore?: () => void
}) {
  const visible = visibleWhatsNew(entries, itemLimit)
  return (
    <div className="min-h-0 space-y-5 overflow-y-auto pr-1">
      {visible.entries.map((entry) => (
        <section key={entry.version} className="space-y-2">
          <h3 className="text-sm font-semibold">
            v{entry.version} <span aria-hidden="true">·</span>{' '}
            <time dateTime={entry.date} className="font-normal text-muted-foreground">{entry.date}</time>
          </h3>
          <ul className="list-disc space-y-1 pl-5 text-sm text-foreground/90">
            {entry.items.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </section>
      ))}
      {visible.remainingItems > 0 ? (
        <button
          type="button"
          className="rounded-sm text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          data-testid={TEST_IDS.whatsNewMore}
          data-whats-new-remaining={visible.remainingItems}
          onClick={() => {
            if (!onShowMore) throw new Error("Limited What's new list requires a full-list action")
            onShowMore()
          }}
        >
          and {visible.remainingItems} more
        </button>
      ) : null}
    </div>
  )
}

interface WhatsNewDialogProps {
  mobile: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function WhatsNewDialog({ mobile, open, onOpenChange }: WhatsNewDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={TEST_IDS.whatsNewDialog}
        data-presentation={mobile ? 'sheet' : 'dialog'}
        className={mobile
          ? 'bottom-0 left-0 top-auto max-h-[85dvh] w-full max-w-none translate-x-0 translate-y-0 grid-rows-[auto_minmax(0,1fr)] rounded-b-none rounded-t-2xl'
          : 'max-h-[80dvh] grid-rows-[auto_minmax(0,1fr)]'}
      >
        <DialogHeader>
          <DialogTitle>What's new</DialogTitle>
          <DialogDescription>User-visible changes, newest first.</DialogDescription>
        </DialogHeader>
        <WhatsNewList entries={WHATS_NEW} />
      </DialogContent>
    </Dialog>
  )
}

export function WhatsNewNotice({ mobile }: { mobile: boolean }) {
  const lastSeenVersion = useWhatsNewLastSeenVersion()
  const [showFullList, setShowFullList] = useState(false)
  const newerEntries = lastSeenVersion === null
    ? []
    : WHATS_NEW.filter((entry) => compareSemver(entry.version, lastSeenVersion) > 0)
  const currentIsNewer = lastSeenVersion !== null
    && compareSemver(lastSeenVersion, __APP_VERSION__) < 0

  useEffect(() => {
    if (lastSeenVersion === null || (currentIsNewer && newerEntries.length === 0)) {
      markWhatsNewSeen()
    }
  }, [currentIsNewer, lastSeenVersion, newerEntries.length])

  const open = currentIsNewer && newerEntries.length > 0
  const dismiss = () => markWhatsNewSeen()
  return (
    <Dialog open={open} onOpenChange={(nextOpen) => {
      if (!nextOpen) dismiss()
    }}>
      <DialogContent
        data-testid={TEST_IDS.whatsNewNotice}
        data-presentation={mobile ? 'sheet' : 'dialog'}
        className={mobile
          ? 'bottom-0 left-0 top-auto max-h-[85dvh] w-full max-w-none translate-x-0 translate-y-0 grid-rows-[auto_minmax(0,1fr)_auto] rounded-b-none rounded-t-2xl'
          : 'max-h-[80dvh] grid-rows-[auto_minmax(0,1fr)_auto]'}
      >
        <DialogHeader>
          <DialogTitle>{showFullList ? "What's new" : 'New since your last visit'}</DialogTitle>
          <DialogDescription>Updates you can see and use.</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 overflow-hidden">
          <WhatsNewList
            entries={newerEntries}
            itemLimit={showFullList ? undefined : NOTICE_ITEM_LIMIT}
            onShowMore={() => setShowFullList(true)}
          />
        </div>
        <div className="flex items-center justify-end">
          <Button type="button" data-testid={TEST_IDS.whatsNewGotIt} onClick={dismiss}>Got it</Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
